import { ActionResponse, DomainType, UIFeedbackHooks } from "@/Shared/Types";
import { ModalHook } from "./TechnicalHooks";
import { ActionGroups, DOMAIN_CONFIGS, DomainFormHook, DomainFormProps, useUIActions } from "@/Shared";
import { useAppNavigation } from "./useAppNavigation";
import { useEffect, useRef } from "react";
import {  useAppDispatch, useAppSelector, usePermission } from "@/OneLiners";
import { uiStateActions } from "../Redux";
import { useAccessRight } from "@/Domains";
import { ActionStatusHelper } from "@/Shared/Helpers";


export function useDomainForm(
    domainType: DomainType,
    uiFeedbackHooks: UIFeedbackHooks = {} as UIFeedbackHooks,
    inModalHook?: ModalHook,
    extraArgs?: {
        entityId?: number
    }
): {
    domainFormHook: DomainFormHook,
    DomainForm: React.ComponentType<DomainFormProps> | undefined,
    domainLabel: string,
    response: ActionResponse,
    UIActions: ActionGroups
} {

    const dispatch = useAppDispatch();
    const selfBadges = useAppSelector(state => state.uiState.badges)?.[domainType];

    /** Resolve domain-specific detail configuration */
    const { useFormHook, DomainForm, domainLabel } = DOMAIN_CONFIGS[domainType];

    const { goToErrorPage } = useAppNavigation();

    const permissionHook = usePermission();

    const accessRight = useAccessRight(permissionHook.dataMap);


    /** Instantiate the domain-specific detail hook with UI feedback */
    const domainFormHook = useFormHook?.({
        permissionHook,
        accessRight,
        uiFeedbackHooks,
        inModalHook,
        extraArgs
    }) as DomainFormHook;

    const { response: formHookResponse, requestId, computeUIActions } = domainFormHook;

    const response = {
        status: ActionStatusHelper.combine(
            formHookResponse.status,
            permissionHook.stateResponse.status,
             //accessRight.status
        ),
        error: formHookResponse.error ??
            permissionHook.stateResponse.error
    }

    /**
     * Guard to prevent multiple redirects during async state transitions
     */
    const hasRedirected = useRef(false);

    const { computeGlobalUIActions } = useUIActions({
        inModal: !!inModalHook,
        modalHook: inModalHook ?? uiFeedbackHooks.modalHook,
        domainType,
        accessRight,
        id: requestId,
        isForm: true
    });

    useEffect(() => {
        if (!accessRight?.hasEnums || hasRedirected.current) return;

        if (ActionStatusHelper.isSucceeded(response.status) && !accessRight.canWrite(domainType)) {
            hasRedirected.current = true;
            goToErrorPage(403);
        }
    }, [accessRight, domainType, response.status]);


    /** Sync current domain type with global UI state **/
    useEffect(() => {
        dispatch(uiStateActions.setCurDomainType(domainType));

        if (requestId && selfBadges?.includes(requestId)) {
            dispatch(
                uiStateActions.removeBadge({
                    key: domainType,
                    values: [requestId]
                })
            );
        }
    }, [domainType, dispatch, requestId]);


    const UIActions = [
        ...computeGlobalUIActions(),
        ...(computeUIActions?.() ?? [])
    ].reduce<ActionGroups>(
        (acc, action) => {
            acc[action.placement].push(action);
            return acc;
        },
        {
            header: [],
            sub: [],
            media: []
        }
    );

    return {
        DomainForm,
        domainLabel,
        domainFormHook,
        response,
        UIActions
    };
}